import { type FormEvent, useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router';

import { useAuth, usePortalSession } from '../auth';
import {
  createPortalEvent,
  deletePortalEvent,
  getPortalDisplayNumbers,
  getPortalEvents,
  PortalApiError,
  updatePortalEvent,
} from '../services/portalApi';
import type { PortalDisplayNumber, PortalEvent, PortalEventInput } from '../types/api';

interface EventFormState {
  name: string;
  description: string;
  startDate: string;
  endDate: string;
  callerDisplayNumberId: string;
}

const emptyForm: EventFormState = {
  name: '',
  description: '',
  startDate: '',
  endDate: '',
  callerDisplayNumberId: '',
};

export function EventsPage() {
  const { getAccessToken } = useAuth();
  const { user } = usePortalSession();
  const [events, setEvents] = useState<PortalEvent[]>([]);
  const [displayNumbers, setDisplayNumbers] = useState<PortalDisplayNumber[]>([]);
  const [form, setForm] = useState<EventFormState>(emptyForm);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<PortalEvent | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [isBusy, setIsBusy] = useState(true);

  const loadEvents = useCallback(async (): Promise<void> => {
    setIsBusy(true);
    setMessage(null);
    try {
      const token = await getAccessToken();
      const response = await getPortalEvents(token);
      setEvents(response.events);
    } catch (error) {
      setMessage(toMessage(error));
    } finally {
      setIsBusy(false);
    }
  }, [getAccessToken]);

  useEffect(() => {
    let cancelled = false;

    async function loadInitialEvents(): Promise<void> {
      try {
        const token = await getAccessToken();
        const [eventResponse, displayNumberResponse] = await Promise.all([
          getPortalEvents(token),
          getPortalDisplayNumbers(token),
        ]);
        if (!cancelled) {
          setEvents(eventResponse.events);
          setDisplayNumbers(displayNumberResponse.displayNumbers);
        }
      } catch (error) {
        if (!cancelled) setMessage(toMessage(error));
      } finally {
        if (!cancelled) setIsBusy(false);
      }
    }

    void loadInitialEvents();
    return () => { cancelled = true; };
  }, [getAccessToken]);

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setMessage(null);

    const start = parseDateInput(form.startDate);
    const inclusiveEnd = parseDateInput(form.endDate);

    if (inclusiveEnd < start) {
      setMessage('Event end date must be the same as or after the start date.');
      return;
    }

    const exclusiveEnd = new Date(inclusiveEnd);
    exclusiveEnd.setDate(exclusiveEnd.getDate() + 1);

    setIsBusy(true);
    const input: PortalEventInput = {
      name: form.name,
      description: form.description,
      startUtc: start.toISOString(),
      endUtc: exclusiveEnd.toISOString(),
      timeZoneId: 'Europe/Oslo',
      callerDisplayNumberId: form.callerDisplayNumberId,
    };

    try {
      const token = await getAccessToken();
      if (editingId) {
        await updatePortalEvent(token, editingId, input);
      } else {
        await createPortalEvent(token, input);
      }
      setForm(emptyForm);
      setEditingId(null);
      await loadEvents();
    } catch (error) {
      setMessage(toMessage(error));
      setIsBusy(false);
    }
  }

  async function confirmDelete(): Promise<void> {
    if (!deleteTarget) return;

    setIsBusy(true);
    setMessage(null);
    try {
      const token = await getAccessToken();
      await deletePortalEvent(token, deleteTarget.EventId);
      setDeleteTarget(null);
      if (editingId === deleteTarget.EventId) {
        setEditingId(null);
        setForm(emptyForm);
      }
      await loadEvents();
    } catch (error) {
      setMessage(toMessage(error));
      setIsBusy(false);
      setDeleteTarget(null);
    }
  }

  function beginEdit(item: PortalEvent): void {
    const inclusiveEnd = new Date(item.EndUtc);
    inclusiveEnd.setMilliseconds(inclusiveEnd.getMilliseconds() - 1);

    setEditingId(item.EventId);
    setForm({
      name: item.Name,
      description: item.Description ?? '',
      startDate: toDateInput(item.StartUtc),
      endDate: toDateInput(inclusiveEnd),
      callerDisplayNumberId: item.CallerDisplayNumberId ?? '',
    });
    setMessage(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  return (
    <section className="events-page">
      {user?.isAdministrator ? (
        <Link className="back-link" to="/admin">← Back to administration</Link>
      ) : null}
      <div className="section-heading">
        <div>
          <p className="eyebrow">Events</p>
          <h1>Events</h1>
          <p className="hero-description">
            An Event is used to reserve phone numbers so that you know you
            have the numbers you need.
          </p>
          <p>
            External users must be part of
            an event to be able to book a phone.<br/>
            Write proper text for Event <strong>Name</strong> and <strong>Description</strong>.
            This is used in the invite
            email.
          </p>
        </div>
      </div>

      {user?.canCreateEvents ? (
      <form className="event-form" onSubmit={(event) => void handleSubmit(event)}>
        <h2>{editingId ? 'Edit event' : 'New event'}</h2>
        <label>
          Name
          <input required maxLength={150} value={form.name}
            onChange={(event) => setForm({ ...form, name: event.target.value })} />
        </label>
        <label>
          Description
          <textarea value={form.description}
            onChange={(event) => setForm({ ...form, description: event.target.value })} />
        </label>

        <label>
          Caller display number
          <select required value={form.callerDisplayNumberId}
            onChange={(event) => setForm({
              ...form,
              callerDisplayNumberId: event.target.value,
            })}>
            <option value="">Select a display number</option>
            {displayNumbers.filter((item) => item.IsActive).map((item) => (
              <option key={item.DisplayNumberId} value={item.DisplayNumberId}>
                {item.Name} — {item.PhoneNumber}
              </option>
            ))}
          </select>
        </label>

        <div className="event-form-dates">
          <label>
            Starts
            <input required type="date" value={form.startDate}
              onChange={(event) => setForm({ ...form, startDate: event.target.value })} />
          </label>
          <label>
            Ends
            <input required type="date" min={form.startDate} value={form.endDate}
              onChange={(event) => setForm({ ...form, endDate: event.target.value })} />
          </label>
        </div>

        <div className="event-form-hint">
          <p>
            Both dates are included. An event from 30.08.2026 to 30.08.2026
            remains active for the entire day.
          </p>
          <p>
            The event name and description will be included in invitation
            emails sent to external participants.
          </p>
        </div>

        <div className="event-form-actions">
          <button className="primary-button" disabled={isBusy} type="submit">
            {editingId ? 'Save changes' : 'Create event'}
          </button>
          {editingId ? (
            <>
              <Link className="secondary-button button-link"
                to={`/events/${editingId}`}>
                Manage phones and participants
              </Link>
              <button className="secondary-button" type="button"
                onClick={() => { setEditingId(null); setForm(emptyForm); setMessage(null); }}>
                Cancel
              </button>

            </>
          ) : null}
        </div>
      </form>
      ) : null}

      {message ? <p className="api-message" role="alert">{message}</p> : null}

      <div className="event-list">
        {isBusy && events.length === 0 ? <p>Loading events...</p> : null}
        {!isBusy && !message && events.length === 0 ? (
          <div className="event-empty-state">
            <h2>No events yet</h2>
            <p>{user?.canCreateEvents ? 'Create the first event using the form above.' : 'No events have been assigned to you.'}</p>
          </div>
        ) : null}
        {events.map((item) => (
          <article className="event-card" key={item.EventId}>
            <div>
              <span className={`event-status event-status-${item.Status.toLowerCase()}`}>
                {item.Status}
              </span>
              <h2>{item.Name}</h2>
              {item.Description ? <p>{item.Description}</p> : null}
              <small>{formatDateRange(item.StartUtc, item.EndUtc)}</small>
              <small className="event-display-number">
                {formatDisplayNumber(item.CallerDisplayNumberId, displayNumbers)}
              </small>
            </div>
            <div className="event-card-actions">
              <Link className="secondary-button button-link"
                to={`/events/${item.EventId}`}>
                {item.CanManage ? 'Manage event' : 'View event'}
              </Link>
              {item.CanManage ? (
                <button className="secondary-button" type="button"
                  onClick={() => beginEdit(item)}>Edit</button>
              ) : null}
              {item.CanDelete ? (
                <button className="icon-button delete-event-button" type="button"
                  aria-label={`Delete ${item.Name}`}
                  title={`Delete ${item.Name}`}
                  onClick={() => setDeleteTarget(item)}>
                  <TrashIcon />
                </button>
              ) : null}
            </div>
          </article>
        ))}
      </div>

      {deleteTarget ? (
        <div className="modal-backdrop" role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setDeleteTarget(null);
          }}>
          <section className="confirmation-dialog" role="dialog" aria-modal="true"
            aria-labelledby="delete-event-title">
            <p className="eyebrow">Delete event</p>
            <h2 id="delete-event-title">
              Do you want to delete {deleteTarget.Name}?
            </h2>
            <p>This action cannot be undone.</p>
            <div className="confirmation-dialog-actions">
              <button className="danger-button" type="button" disabled={isBusy}
                onClick={() => void confirmDelete()}>
                Delete
              </button>
              <button className="secondary-button" type="button" disabled={isBusy}
                onClick={() => setDeleteTarget(null)}>
                Cancel
              </button>
            </div>
          </section>
        </div>
      ) : null}
    </section>
  );
}

function TrashIcon() {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24" width="20" height="20"
      fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <path d="M3 6h18" />
      <path d="M8 6V4h8v2" />
      <path d="M19 6l-1 14H6L5 6" />
      <path d="M10 11v5M14 11v5" />
    </svg>
  );
}

function parseDateInput(value: string): Date {
  const [year, month, day] = value.split('-').map(Number);
  return new Date(year, month - 1, day);
}

function toDateInput(value: string | Date): string {
  const date = typeof value === 'string' ? new Date(value) : value;
  const year = date.getFullYear().toString().padStart(4, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function formatDateRange(startValue: string, exclusiveEndValue: string): string {
  const inclusiveEnd = new Date(exclusiveEndValue);
  inclusiveEnd.setMilliseconds(inclusiveEnd.getMilliseconds() - 1);
  return `${formatDate(startValue)} – ${formatDate(inclusiveEnd)}`;
}

function formatDate(value: string | Date): string {
  const date = typeof value === 'string' ? new Date(value) : value;
  return new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    timeZone: 'Europe/Oslo',
  }).format(date);
}

function formatDisplayNumber(
  displayNumberId: string | null,
  displayNumbers: PortalDisplayNumber[],
): string {
  if (!displayNumberId) return 'Caller display number not configured';
  const item = displayNumbers.find(
    (displayNumber) => displayNumber.DisplayNumberId === displayNumberId,
  );
  return item ? `Caller display number: ${item.Name} — ${item.PhoneNumber}`
    : 'Caller display number unavailable';
}

function toMessage(error: unknown): string {
  return error instanceof PortalApiError || error instanceof Error
    ? error.message
    : 'Something went wrong. Please try again.';
}
